// merge two arrays of objects + dedupe using defined prop

import { matchPath } from 'react-router-dom';
import { PAGE_TITLES, PATHS } from 'routes/routes.constants';

export const getPageTab = (pathname: string) => {
  const paths = Object.entries(PATHS) as [keyof typeof PATHS, string][];
  const path = paths.find((p: [string, string]) =>
    matchPath({ path: p[1] }, pathname),
  );
  if (path?.length) {
    return PAGE_TITLES[path[0]];
  }
  return 'Unknown tab';
};

type RequiredNestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

type NotArray<T> = T extends any[] ? never : T;

export type NestedKeyOf<ObjectType extends Partial<object>> =
  RequiredNestedKeyOf<Required<NotArray<ObjectType>>>;

export type NestedPropertyType<T, K extends string> = K extends keyof T
  ? T[K]
  : K extends `${infer FirstPart}.${infer Rest}`
    ? FirstPart extends keyof T
      ? NestedPropertyType<Exclude<T[FirstPart], undefined>, Rest>
      : never
    : never;

export type PropValueUpdater<T extends Partial<object>> = (
  p: NestedKeyOf<T>,
  v: NestedPropertyType<T, NestedKeyOf<T>>,
) => void;

export const updateNestedProperty = <
  T extends { [key: string]: any },
  P extends NestedKeyOf<T>,
>(
  object: T,
  propertyPath: P,
  value: NestedPropertyType<T, P>,
): T => {
  const properties = propertyPath.split('.');
  const property = properties.shift();

  if (!property) {
    return object;
  }

  let updatedObject = object;
  if (properties.length === 0) {
    updatedObject = {
      ...object,
      [property]: value,
    };
  } else {
    updatedObject = {
      ...object,
      [property]: updateNestedProperty(
        object[property] || {},
        properties.join('.'),
        value,
      ),
    };
  }

  return updatedObject as T;
};

export const pick = <T extends { [key: string]: any }, K extends keyof T>(
  obj: T,
  ...props: K[]
): Pick<T, K> => {
  return props.reduce(
    (picked, key) => ({
      ...picked,
      [key]: obj[key],
    }),
    {} as Pick<T, K>,
  );
};

/**
 * Picks properties from an object, supporting both top-level and nested paths.
 * Nested paths use dot notation (e.g., 'latencyControl.initialDelay').
 *
 * @param {T} obj - The object to pick properties from
 * @param {string[]} keys - Array of keys, which can be top-level (e.g., 'name') or nested (e.g., 'latencyControl.initialDelay')
 * @return {Partial<T>} An object containing only the specified properties
 *
 * @example
 * const obj = { name: 'test', latencyControl: { initialDelay: 100, interval: 50 } };
 * pickNested(obj, ['name', 'latencyControl.initialDelay']);
 * // Returns: { name: 'test', latencyControl: { initialDelay: 100 } }
 */
export const pickNested = <T extends { [key: string]: any }>(
  obj: T,
  keys: string[],
): Partial<T> => {
  if (!obj || !keys.length) {
    return {};
  }

  const result: Partial<T> = {};

  keys.forEach(key => {
    if (key.includes('.')) {
      // Handle nested path
      const pathParts = key.split('.');
      const topLevelKey = pathParts[0];
      const nestedPath = pathParts.slice(1).join('.');

      if (obj[topLevelKey] !== undefined && obj[topLevelKey] !== null) {
        // Get the nested value
        const nestedValue = getNestedValue(obj[topLevelKey], nestedPath);

        if (nestedValue !== undefined) {
          // Initialize the top-level key if it doesn't exist in result
          if (!(topLevelKey in result)) {
            (result as any)[topLevelKey] = {};
          }

          // Set the nested value
          setNestedValue((result as any)[topLevelKey], nestedPath, nestedValue);
        }
      }
    } else {
      // Handle top-level key
      if (key in obj) {
        (result as any)[key] = obj[key];
      }
    }
  });

  return result;
};

/**
 * Gets a value from an object using a dot-notation path.
 *
 * @param {any} obj - The object to get the value from
 * @param {string} path - The dot-notation path (e.g., 'latencyControl.initialDelay')
 * @return {any} The value at the specified path, or undefined if not found
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
};

/**
 * Sets a value in an object using a dot-notation path, creating nested objects as needed.
 *
 * @param {any} obj - The object to set the value in
 * @param {string} path - The dot-notation path (e.g., 'latencyControl.initialDelay')
 * @param {any} value - The value to set
 * @return {void}
 */
const setNestedValue = (obj: any, path: string, value: any): void => {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (
      !(key in current) ||
      typeof current[key] !== 'object' ||
      current[key] === null
    ) {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
};
